<script>
import ProjectFilter from "./ProjectFilter.vue";
import { arrowDownIcon, crossCircleIcon, filterIcon, loadingSmIcon } from "@/utils/proposalIcons";

export default {
  name: "SelectDropdown",
  components: {
    ProjectFilter,
  },
  props: {
    modelValue: {
      type: [Array, String, Number, Object, null],
      default: null,
    },
    disabled: { type: Boolean, default: false },
    className: { type: String, default: "" },
    showSearch: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select an option" },
    searchPlaceholder: { type: String, default: "Search..." },
    multi: { type: Boolean, default: false },
    maxDisplay: { type: Number, default: 2 },
    maxSelection: { type: Number, default: Infinity },
    options: {
      type: Array,
      default: () => [],
    },
    filterable: { type: Boolean, default: true },
    projectFilters: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    canPaginate: { type: Boolean, default: false },
    isClearable: { type: Boolean, default: false },
    searchQuery: { type: String, default: "" },
    selectedSegment: { type: [String, Number], default: "All Segments" },
    dateFrom: { type: String, default: "" },
    dateTo: { type: String, default: "" },
    label: { type: String, default: "" },
    isPreviewable: { type: Boolean, default: false },
    isFontFamily: { type: Boolean, default: false },
    noResultsText: { type: String, default: "No results found" },
  },
  data() {
    return {
      isOpen: false,
      showFilters: false,
      hoveredOption: null,
      dropdownId: Math.floor(Math.random() * 1000),
      arrowDownIcon, crossCircleIcon, filterIcon, loadingSmIcon
    };
  },
  computed: {
    filteredOptions() {
      let opts = this.options;
      if (this.projectFilters) return opts;
      if (this.filterable && this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        opts = opts.filter((option) =>
          this.getOptionLabel(option).toLowerCase().includes(query)
        );
      }
      return opts;
    },
    selectedValue() {
      return this.multi ? (Array.isArray(this.modelValue) ? this.modelValue : []) : this.modelValue;
    },
    allSelected() {
      if (!this.multi || this.filteredOptions.length === 0) return false;
      return this.filteredOptions.every((option) =>
        this.selectedValue.includes(this.getOptionValue(option))
      );
    },
    someSelected() {
      if (!this.multi || this.filteredOptions.length === 0) return false;
      const selectedCount = this.filteredOptions.filter((option) =>
        this.selectedValue.includes(this.getOptionValue(option))
      ).length;
      return selectedCount > 0 && selectedCount < this.filteredOptions.length;
    },
    displayText() {
      if (!this.multi) {
        if (this.selectedValue === null || this.selectedValue === undefined || this.selectedValue === "") return this.placeholder;
        const selectedOption = this.options.find(
          (o) => this.getOptionValue(o) === this.selectedValue
        );
        return selectedOption
          ? this.getOptionLabel(selectedOption)
          : String(this.selectedValue);
      }
      if (this.selectedValue.length === 0) return this.placeholder;
      const selectedOptions = this.options.filter((o) =>
        this.selectedValue.includes(this.getOptionValue(o))
      );
      const labels = selectedOptions.slice(0, this.maxDisplay).map(this.getOptionLabel);
      return selectedOptions.length > this.maxDisplay
        ? `${labels.join(", ")} +${selectedOptions.length - this.maxDisplay} more`
        : labels.join(", ");
    }
  },
  mounted() {
    document.addEventListener("mousedown", this.handleOutside);
	console.log("In SelectDropdown mounted");
	console.log(this.canPaginate);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutside);
  },
  methods: {
    getOptionLabel(option) {
      return typeof option === "object" && option !== null
        ? option.label !== undefined
          ? option.label
          : option.value !== undefined
            ? option.value
            : ""
        : String(option);
    },
    getOptionValue(option) {
      return typeof option === "object" && option !== null
        ? option.value !== undefined
          ? option.value
          : option.label !== undefined
            ? option.label
            : option
        : option;
    },
    isSelected(option) {
      const value = this.getOptionValue(option);
      return this.multi
        ? this.selectedValue.includes(value)
        : this.selectedValue === value;
    },
    isOptionDisabled(option) {
      if (this.loading) return true;
      if (!this.multi) return false;
      if (this.isSelected(option)) return false;
      return this.selectedValue.length >= this.maxSelection;
    },
    handleOptionClick(option) {
      if (this.isOptionDisabled(option)) return;
      if (this.isPreviewable) {
        this.hoveredOption = option;
      }
      const value = this.getOptionValue(option);
      if (this.multi) {
        const currentValues = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
        const idx = currentValues.indexOf(value);
        if (idx > -1) currentValues.splice(idx, 1);
        else currentValues.push(value);
        this.$emit("update:modelValue", currentValues);
      } else {
        this.$emit("update:modelValue", value);
        this.isOpen = false;
      }
    },
    toggleSelectAll() {
      if (!this.multi) return;
      if (this.allSelected) {
        this.$emit("update:modelValue", []);
      } else {
        const allVisibleValues = this.filteredOptions.map(this.getOptionValue);
        const newSelection = allVisibleValues.slice(0, this.maxSelection);
        this.$emit("update:modelValue", newSelection);
      }
    },
    clearSelection(e) {
      if (e) e.stopPropagation();
      if (this.disabled || this.loading) return;
      this.$emit("update:modelValue", this.multi ? [] : null);
    },
    handleOutside(e) {
      if (!e.target.closest(`.dropdown-${this.dropdownId}`)) {
        this.isOpen = false;
        this.showFilters = false;
      }
    },
    onPreviewMouseLeave() {
      if (this.isPreviewable) {
        const option = this.options.find((option) => option.value === this.modelValue);
        this.hoveredOption = option ? option : null;
      }
    },
    handlePagination() {
      this.$emit("handlePagination");
    }
  }
};
</script>

<template>
  <div class="w-100">
    <label v-if="label" class="label" :for="modelValue">{{ label }}</label>
    <div :class="[
      `dropdown-${dropdownId}`,
      'searchable-dropdown',
    ]">
      <!-- Trigger -->
      <div class="searchable-dropdown-trigger" @click="!disabled && !loading && (isOpen = !isOpen)"
        :class="{ disabled, loading }">
        <span class=" searchable-dropdown-text" :class="{
          placeholder: multi ? selectedValue.length === 0 : (selectedValue === null || selectedValue === undefined || selectedValue === ''),
        }">
          {{ displayText || placeholder }}
        </span>

        <span v-if="isClearable && (multi ? selectedValue.length > 0 : selectedValue)" class="remove-btn"
          v-html="crossCircleIcon" @click="clearSelection" />
        <span v-if="loading" v-html="loadingSmIcon" class="spinner"></span>

        <span v-else v-html="arrowDownIcon" class="searchable-dropdown-arrow" :class="{ open: isOpen }" />
      </div>

      <!-- Dropdown -->
      <div v-if="isOpen" class="searchable-dropdown-menu" :class="{ disabled, loading }">
        <!-- Search Input & Filter Icon -->
        <div v-if="showSearch" class="searchable-dropdown-search">
          <div class="search-input-wrapper">
            <input type="text" class="searchable-dropdown-search-input" :placeholder="searchPlaceholder"
              :disabled="loading || disabled" :value="searchQuery"
              @input="$emit('update:searchQuery', $event.target.value)" @click="(e) => e.stopPropagation()"
              ref="searchInput" />
            <button v-if="projectFilters" class="filter-toggle-btn" :class="{ active: showFilters }"
              :disabled="loading || disabled" @click.stop="showFilters = !showFilters">
              <span v-html="filterIcon" />
            </button>
          </div>

          <!-- Project Filters Panel and Pills -->
          <div v-if="projectFilters">
            <ProjectFilter :selectedSegment="selectedSegment" :dateFrom="dateFrom" :dateTo="dateTo"
              @update:selectedSegment="$emit('update:selectedSegment', $event)"
              @update:dateFrom="$emit('update:dateFrom', $event)" @update:dateTo="$emit('update:dateTo', $event)"
              @apply="$emit('apply-filters'); showFilters = false" @reset="$emit('reset-filters')"
              @close="showFilters = false" :showFilters="showFilters" />
          </div>
        </div>

        <!-- Multi-select actions -->
        <div v-if="multi && filteredOptions.length > 1 && maxSelection == Infinity" class="searchable-dropdown-option"
          @click="toggleSelectAll">
          <span class="checkbox" :class="{
            checked: allSelected,
            indeterminate: someSelected && !allSelected,
            disabled: loading,
          }">
            <span class="checkmark" v-if="allSelected">✓</span>
          </span>
          <span class="select-all-label"> Select All </span>
        </div>

        <!-- Options List -->
        <div class="searchable-dropdown-options" @mouseleave="onPreviewMouseLeave">
          <div class="searchable-dropdown-options-list">
            <div v-for="(option, index) in filteredOptions" :key="index" class="searchable-dropdown-option" :class="{
              selected: isSelected(option),
              multi: multi,
            }" @click="handleOptionClick(option)" @mouseenter="hoveredOption = option">
              <!-- Checkbox for multi-select -->
              <span v-if="multi" class="checkbox" :class="{
                checked: isSelected(option),
                disabled: isOptionDisabled(option),
              }">
                <span v-if="isSelected(option)" class="checkmark">✓</span>
              </span>

              <span class="option-label" :style="[
                isFontFamily ? { fontFamily: getOptionValue(option), fontWeight: 'bold' } : {}
              ]">{{ getOptionLabel(option)
              }}</span>
            </div>

            <!-- No results message -->
            <div v-if="filteredOptions.length === 0" class="searchable-dropdown-no-results">
              {{ noResultsText }}
            </div>
          </div>

          <!-- Preview Tooltip -->
          <div v-if="isPreviewable && filteredOptions.length > 0" class="searchable-dropdown-preview-box">
            <div v-if="hoveredOption && hoveredOption.preview" class="preview"
              :class="hoveredOption.orientation === 'portrait' ? 'portrait' : 'landscape'">
              <img :src="hoveredOption.preview" alt="Preview" />
            </div>
            <span v-else class="preview-text">Preview</span>
          </div>
        </div>

        <!-- Selected count for multi-select -->
        <div v-if="multi && selectedValue.length > 0" class="searchable-dropdown-selected-count">
          {{ selectedValue.length }} item{{
            selectedValue.length !== 1 ? "s" : ""
          }}
          selected
        </div>

        <!-- Load more button -->
        <button v-if="canPaginate" class="load-more-button" :disabled="loading" @click="handlePagination">Load More
          <span v-if="loading" class="spinner" v-html="loadingSmIcon" /></button>
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">
.label {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
}

.searchable-dropdown {
  position: relative;
  display: inline-block;
  width: 100%;

  &.disabled,
  &.loading {
    pointer-events: none;
  }

  &-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #ddd;
    padding: 8px 12px;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s;
    min-height: 40px;

    &:hover {
      border-color: #aaa;
    }

    &.disabled,
    &.loading {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .searchable-dropdown-text {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.placeholder {
        color: #999;
      }
    }

    .searchable-dropdown-arrow {
      margin-left: 4px;
      transition: transform 0.2s;
      font-size: 12px;
      color: #999;
      flex-shrink: 0;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  &-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 999;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    margin-top: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    &.disabled,
    &.loading {
      background: #f8f9fa;
    }
  }

  &-search {
    padding: 8px;
    border-bottom: 1px solid #eee;

    .search-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .searchable-dropdown-search-input {
      width: 100%;
      padding: 8px 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      outline: none;

      &:focus {
        border-color: #4a90e2;
      }
    }

    .filter-toggle-btn {
      background: none;
      border: 1px solid #ddd;
      background: white;
      border-radius: 4px;
      padding: 9.5px;
      cursor: pointer;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      &:hover {
        background: #f5f5f5;
        color: #4a90e2;
        border-color: #4a90e2;
      }

      &.active {
        background: #e6f3ff;
        color: #4a90e2;
        border-color: #4a90e2;
      }

      &:disabled {
        opacity: 0.6;

        &:hover {
          cursor: default;
          border: 1px solid #ddd;
          color: #666;
          background: white;
        }
      }
    }
  }

  &-options {
    display: flex;
    flex-direction: row;

    @media (max-width: 768px) {
      flex-direction: column-reverse;
    }
  }

  &-options-list {
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
  }

  &-option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }

    &.selected:not(.multi) {
      background-color: #e6f3ff;
      color: #4a90e2;
    }

    .checkbox {
      width: 16px;
      height: 16px;
      border: 2px solid #ddd;
      border-radius: 3px;
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      background: white;
      flex-shrink: 0;

      &.checked {
        background-color: #4a90e2;
        border-color: #4a90e2;

        .checkmark {
          color: white;
          font-size: 12px;
          font-weight: bold;
        }
      }

      &.disabled {
        opacity: 0.7;
        background: #ddd;
        pointer-events: none;
      }
    }

    .option-label {
      flex: 1;
    }

    .selected-indicator {
      margin-left: auto;
      color: #4a90e2;
      font-weight: bold;
    }
  }

  &-preview-box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid #ddd;
    width: 130px;
    min-height: 120px;
    flex-shrink: 0;

    @media screen and (max-width: 768px) {
      width: 100%;
      border-left: none;
      border-bottom: 1px solid #ddd;
      min-height: 120px;
    }

    .preview {
      position: absolute;
      top: 50%;
      left: 46%;
      transform: translate(-50%, -50%);
      margin: 0px 5px;
      border: 1px solid #ddd;
      z-index: 20;
      pointer-events: auto;
      overflow: hidden;
      cursor: zoom-in;

      &:hover {
        transform: scale(1.8);
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: fill;
        transition: transform 0.3s ease-in-out;
      }
    }

    .preview-text {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      color: #727070;
      font-size: 10px;
      margin: 0;
    }
  }

  .portrait {
    width: 80px;
    height: 110px;
  }

  .landscape {
    width: 110px;
    height: 80px;
  }

  &-no-results {
    padding: 12px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }

  &-selected-count {
    padding: 5px 12px;
    border-top: 1px solid #eee;
    font-size: 12px;
    color: #666;
    background: #f8f9fa;
  }

  .load-more-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    gap: 5px;
    cursor: pointer;
    font-size: 12px;
    color: white;
    background: #4475b4;
    border: 1px solid transparent;
    transition: all 0.2s;
    width: 100%;
    font-weight: 600;

    &:hover {
      background: #4a90e2;
      color: #fff;
    }

    &:disabled {
      background: #ccc;
      color: #999;
      cursor: not-allowed;
    }
  }

  .remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ef4444;
    cursor: pointer;
    margin-left: 4px;
    flex-shrink: 0;
    transition: all 0.2s;
    width: 14px;
    height: 14px;

    &:hover {
      color: #da1c1c;
    }
  }
}
</style>

<style>
.spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
}

::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
